/**
 * Parse and evaluate Excel-style formulas in DOCX forms
 * Formulas can start with "=" or without it, and reference other fields by itemId
 * Example: "=ItemId1 * ItemId2" or "ItemId1 + ItemId2 - ItemId3"
 */

import { format as numfmtFormat } from 'numfmt';

export interface FormulaContext {
  getFieldValue: (itemId: string) => number;
}

/**
 * Strip currency symbols and other non-numeric characters from a string
 * Keeps decimal points and minus signs for negative numbers
 * @param value - String value that might contain currency symbols
 * @returns Cleaned numeric string
 */
export function stripCurrencySymbols(value: string): string {
  if (!value) return '';
  
  // List of common currency symbols and characters to remove
  const currencySymbols = /[$€£¥₹₽¢₩₪₨₱₡₦₵₴₸₺₲₾〰]/g;
  
  // Remove currency symbols
  let cleaned = value.replace(currencySymbols, '');
  
  // Remove common separators (commas, spaces) but preserve decimal points
  cleaned = cleaned.replace(/[,\s]/g, '');
  
  // Keep only digits, decimal point, and minus sign
  cleaned = cleaned.replace(/[^\d.-]/g, '');
  
  return cleaned.trim();
}

/**
 * Parse an Excel-style formula and convert it to JavaScript
 * @param formula - Excel formula with or without "=" (e.g., "=A1 * B2" or "A1 * B2")
 * @returns JavaScript expression with field references converted to getValue calls
 */
export function parseFormula(formula: string): string | null {
  if (!formula) {
    return null;
  }

  // Remove the leading "=" if present and trim
  let expression = formula.trim();
  if (expression.startsWith('=')) {
    expression = expression.substring(1).trim();
  }

  // Replace field references (itemIds) with getValue calls
  // This regex matches word characters (letters, numbers, underscores) and dots
  // which are common in itemId formats
  expression = expression.replace(/([a-zA-Z_][a-zA-Z0-9_\.]*)/g, (match) => {
    // Check if this is a function name (like SUM, MAX, etc.)
    const upperMatch = match.toUpperCase();
    if (['SUM', 'MAX', 'MIN', 'AVG', 'AVERAGE', 'COUNT'].includes(upperMatch)) {
      return match; // Keep function names as-is for now
    }
    
    // Convert field reference to getValue call
    return `getValue("${match}")`;
  });

  return expression;
}

/**
 * Evaluate a parsed formula expression
 * @param expression - JavaScript expression with getValue references
 * @param context - Context object with getFieldValue function
 * @returns Calculated result or null if evaluation fails
 */
export function evaluateFormula(expression: string, context: FormulaContext): number | null {
  try {
    // Create getValue function for the formula context
    const getValue = (itemId: string): number => {
      return context.getFieldValue(itemId);
    };

    // Evaluate the expression
    // eslint-disable-next-line no-new-func
    const result = new Function('getValue', `return ${expression}`)(getValue);
    
    return typeof result === 'number' && !isNaN(result) ? result : null;
  } catch (error) {
    console.error('Formula evaluation error:', error, 'Expression:', expression);
    return null;
  }
}

/**
 * Extract all field IDs referenced in a formula
 * @param formula - Excel formula with or without "=" prefix
 * @returns Array of itemIds referenced in the formula
 */
export function extractFormulaReferences(formula: string): string[] {
  if (!formula) {
    return [];
  }
  
  // Remove leading = if present
  let expression = formula.trim();
  if (expression.startsWith('=')) {
    expression = expression.substring(1).trim();
  }
  
  // Remove outer brackets if present
  if (expression.charAt(0) === '(' && expression.charAt(expression.length - 1) === ')') {
    expression = expression.substring(1, expression.length - 1).trim();
  }
  
  const references: string[] = [];
  const regex = /([a-zA-Z_][a-zA-Z0-9_\.]*)/g;
  let match;

  while ((match = regex.exec(expression)) !== null) {
    const fieldRef = match[1];
    const upperRef = fieldRef.toUpperCase();
    
    // Skip function names and JavaScript keywords
    if (!['SUM', 'MAX', 'MIN', 'AVG', 'AVERAGE', 'COUNT', 'MATH'].includes(upperRef)) {
      if (!references.includes(fieldRef)) {
        references.push(fieldRef);
      }
    }
  }

  return references;
}

/**
 * Apply numfmt formatting to all elements with data-format attribute in a container
 * @param containerSelector - CSS selector for the container to search within
 * @returns Number of elements formatted
 */
export function formatAllFieldsInContainer(containerSelector: string = '.preview-form-content'): number {
  const container = document.querySelector(containerSelector);
  if (!container) {
    console.warn(`formatAllFieldsInContainer: Container not found with selector "${containerSelector}"`);
    return 0;
  }

  const elementsWithFormat = container.querySelectorAll('[data-format]');
  let formattedCount = 0;

  elementsWithFormat.forEach((element) => {
    const format = element.getAttribute('data-format');
    if (!format) return;

    // Handle input, textarea, select, and label elements
    let value: string | null = null;
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
      value = element.value;
    } else if (element instanceof HTMLLabelElement) {
      value = element.textContent;
    } else {
      value = element.textContent;
    }

    if (!value) return;

    const numericValue = parseFloat(stripCurrencySymbols(value));
    if (isNaN(numericValue)) {
      console.warn(`formatAllFieldsInContainer: Value "${value}" is not a valid number for element with format "${format}"`);
      return;
    }

    try {
      const formattedValue = numfmtFormat(format, numericValue);
      
      if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
        element.value = formattedValue;
      } else {
        element.textContent = formattedValue;
      }
      
      console.log(`Formatted element: ${numericValue} -> ${formattedValue} (format: "${format}")`);
      formattedCount++;
    } catch (error) {
      console.warn(`formatAllFieldsInContainer: Failed to apply format "${format}":`, error);
    }
  });

  console.log(`formatAllFieldsInContainer: Formatted ${formattedCount} elements`);
  return formattedCount;
}

/**
 * Setup formula calculation for a field
 * @param formulaField - The input element that displays the calculated result
 * @param formula - The Excel formula to evaluate
 * @param containerSelector - Selector for the form container
 */
export function setupFormulaCalculation(
  formulaField: HTMLInputElement,
  formula: string,
  containerSelector: string = '.preview-form-content'
): void {
  const container = document.querySelector(containerSelector);
  if (!container) {
    console.warn('Formula setup: Container not found');
    return;
  }

  // Parse the formula
  const parsedExpression = parseFormula(formula);
  if (!parsedExpression) {
    console.warn('Formula setup: Failed to parse formula:', formula);
    return;
  }

  // Extract field references
  const references = extractFormulaReferences(formula);
  console.log(`Formula setup for field:`, formulaField.getAttribute('data-element-id'), 'References:', references);

  // Create context for getting field values
  const context: FormulaContext = {
    getFieldValue: (itemId: string): number => {
      const field = container.querySelector(`[data-element-id="${itemId}"]`) as HTMLInputElement | HTMLSelectElement | null;
      if (!field) {
        console.warn(`Formula: Referenced field not found: ${itemId}`);
        return 0;
      }
      
      // Strip currency symbols and parse the value
      let value = field.value;
      const cleanedValue = stripCurrencySymbols(value);
      
      const numericValue = parseFloat(cleanedValue);
      if (isNaN(numericValue)) {
        console.warn(`Formula: Field ${itemId} value "${value}" could not be converted to a number`);
        return 0;
      }
      
      return numericValue;
    },
  };

  // Calculate function
  const calculate = () => {
    const result = evaluateFormula(parsedExpression, context);
    if (result !== null) {
      // Check if the formula field has a format attribute
      const format = formulaField.getAttribute('data-format');
      let displayValue: string;

      if (format) {
        try {
          // Apply numfmt formatting if format exists
          displayValue = numfmtFormat(format, result);
          console.log(`Applied format "${format}" to result: ${result} -> ${displayValue}`);
        } catch (error) {
          console.warn(`Failed to apply format "${format}":`, error);
          displayValue = result.toString();
        }
      } else {
        displayValue = result.toString();
      }

      formulaField.value = displayValue;
      // Trigger change event so other formulas that depend on this field can update
      formulaField.dispatchEvent(new Event('input', { bubbles: false }));
    }
  };

  // Initial calculation
  calculate();

  // Set up event listeners on referenced fields
  let listenersAttached = 0;
  references.forEach((itemId) => {
    const field = container.querySelector(`[data-element-id="${itemId}"]`);
    if (field) {
      // Remove existing listeners to avoid duplicates
      field.removeEventListener('input', calculate);
      field.removeEventListener('change', calculate);
      // Add new listeners
      field.addEventListener('input', calculate);
      field.addEventListener('change', calculate);
      listenersAttached++;
    } else {
      console.warn(`Formula setup: Could not find field with data-element-id="${itemId}"`);
    }
  });
  
  console.log(`Attached ${listenersAttached} listeners for ${references.length} references`);
}
